import { AlertCircle } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import {
  ReactNode,
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  type AnySpec,
  applyFieldUpdates,
  type KindMap,
  type UnitValues
} from 'v5/core/form-schema/formKinds.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';
import type {
  AnyFieldSpecBase,
  EditUnit,
  EntitySchema,
  FieldsUnit,
  ListUnit,
  ScopeSection
} from 'v5/core/form-schema/formSchema.types';
import {
  Alert,
  AlertCloseButton,
  AlertDescription,
  AlertTitle
} from 'v5/design-system/Alert';
import { Form, FormRootElement } from 'v5/design-system/Form';
import { cn } from 'v5/platform/dom/cn';
import { FieldContextProvider } from './FieldContext';
import { ListRowsField } from './ListRowsField';
import { type ListFormRow, readRowValues, toValuesShape } from './listFormRows';
import { RenderField } from './RenderField';
import type {
  CreateFormValues,
  FormKit,
  SchemaFormHandle,
  SchemaFormProps
} from './schemaForm.types';
import { ScopeSectionView } from './ScopeSectionView';
import { UnitHeading } from './UnitHeading';
import { SectionTabs } from './SectionTabs';
import { type FieldCheck, useFieldsResolver } from './fieldValidation';
import { fieldPath, unitPath } from './unitPath';
import {
  SECTION_SCROLL_MARGIN_CLASS,
  useSectionNavigation
} from './useSectionNavigation';
import { saveErrorMessage } from './saveErrorMessage';

export interface SchemaFormRenderProps<M extends KindMap<M>>
  extends SchemaFormProps<M> {
  /** Submits from a modal's buttons and updates fields from outside. */
  handle?: Ref<SchemaFormHandle<M>>;
  renderSectionIcon?(section: ScopeSection<AnyFieldSpecBase>): ReactNode;
  /** See `SchemaSectionsRenderProps.renderUnitIcon`. */
  renderUnitIcon?(unit: EditUnit<AnySpec<M>>): ReactNode;
  /** Feature content rendered before the units of a section (e.g. the profile picture in Global). */
  renderSectionPrefix?(section: ScopeSection<AnyFieldSpecBase>): ReactNode;
  /** See `SchemaSectionsRenderProps.singleSectionHeader`. */
  singleSectionHeader?: boolean;
}

/**
 * Create mode root: one react-hook-form instance over every unit of every
 * section; values keyed by unit id (`CreateFormValues`). Units that appear
 * or disappear when the schema changes (membership picker) are registered /
 * unregistered with their defaults. Submit is imperative for modal buttons.
 */
export function SchemaForm<M extends KindMap<M>>({
  schema,
  kit,
  handle,
  initialValues,
  onSubmit,
  newListItem,
  onStateChange,
  onValuesChange,
  onSubmitRequest,
  variant,
  size,
  navigation = 'none',
  renderSectionIcon,
  renderUnitIcon,
  renderSectionPrefix,
  singleSectionHeader = true
}: SchemaFormRenderProps<M>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SchemaForm' });
  const sectionNav = useSectionNavigation(schema.sections);
  const singleSection = schema.sections.length === 1;
  const tabs = navigation === 'tabs' && !singleSection;
  const defaultValues = useMemo(
    () => toFormShape(initialValues),
    // initial values are captured once; schema changes are handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const resolver = useFieldsResolver<M, FormShape<M>>(
    data => schemaChecks(schema, data),
    kit
  );
  const form = useForm<FormShape<M>>({
    defaultValues,
    resolver,
    mode: 'onChange',
    reValidateMode: 'onChange'
  });
  const { isSubmitting, isValid, isDirty } = form.formState;

  useEffect(() => {
    onStateChange?.({ isSubmitting, isValid, isDirty });
  }, [isSubmitting, isValid, isDirty, onStateChange]);

  useEffect(() => {
    if (!onValuesChange) return;
    const subscription = form.watch(() => {
      onValuesChange(fromFormShape(schema, form.getValues()));
    });
    return () => subscription.unsubscribe();
  }, [form, schema, onValuesChange]);

  // Register defaults for units that appeared after mount (new sections).
  useEffect(() => {
    const current = form.getValues();
    for (const section of schema.sections) {
      for (const unit of section.units) {
        const path = unitPath(unit.id);
        // Its fields register during render, before this effect, and leave a
        // shape without `type`; checks and submit would read none of it.
        if (current[path]?.type === undefined) {
          const initial = initialValues[unit.id];
          form.setValue(
            path,
            initial ? toUnitShape(initial) : emptyUnitShape(unit.type),
            { shouldDirty: false }
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  const submit = form.handleSubmit(async data => {
    try {
      await onSubmit(fromFormShape(schema, data));
    } catch (e) {
      form.setError('root', {
        message: saveErrorMessage(e, t('saveInvalidData'))
      });
      throw e;
    }
  });

  useImperativeHandle(
    handle,
    () => ({
      submit: async () => submit(),
      updateFields: updates => {
        const data = form.getValues();
        for (const section of schema.sections) {
          for (const unit of section.units) {
            const path = unitPath(unit.id);
            const shape = data[path];
            if (unit.type !== 'fields' || shape?.type !== 'fields') continue;
            const next = applyFieldUpdates(
              unit.fields,
              readRowValues(unit.fields, shape.values),
              updates
            );
            if (next === null) continue;
            form.setValue(
              path,
              { type: 'fields', values: toValuesShape(next) },
              { shouldDirty: true, shouldValidate: true }
            );
          }
        }
      }
    }),
    [submit, form, schema]
  );

  return (
    <Form {...form}>
      <FieldContextProvider
        value={{ mode: 'edit', variant, size, basePath: '', ghost: false }}
      >
        <FormRootElement
          // ⌘/Ctrl+Enter (and the sr-only submit button) go to the host when
          // it owns the save flow; otherwise the form submits itself.
          onSubmit={() => (onSubmitRequest ? onSubmitRequest() : void submit())}
          // Plain Enter in a single-line input would implicitly submit the
          // whole form (a silent create in a dialog); only the explicit
          // shortcut and the footer buttons submit. Buttons and textareas
          // keep their own Enter behaviour.
          onKeyDown={event => {
            if (
              event.key === 'Enter' &&
              !event.metaKey &&
              !event.ctrlKey &&
              event.target instanceof HTMLInputElement
            ) {
              event.preventDefault();
            }
          }}
          // The tab bar brings its own top padding; without it the form pads itself.
          className={cn('flex flex-col gap-6 pb-3', !tabs && 'pt-3')}
          data-testid="schema-form"
        >
          {tabs && (
            <SectionTabs
              sections={schema.sections}
              activeId={sectionNav.activeId}
              onSelect={sectionNav.goTo}
              renderIcon={renderSectionIcon}
            />
          )}
          <div ref={sectionNav.rootRef} className="flex flex-col gap-6">
            {schema.sections.map(section => (
              <ScopeSectionView
                key={section.id}
                section={section}
                icon={renderSectionIcon ? renderSectionIcon(section) : null}
                // Create mode: every section stays open — the form is one
                // document and the tab bar is the navigation.
                alwaysOpen
                showHeader={!singleSection || singleSectionHeader}
                open={sectionNav.isOpen(section)}
                onOpenChange={open => sectionNav.setOpen(section.id, open)}
                className={tabs ? SECTION_SCROLL_MARGIN_CLASS : undefined}
              >
                <div className="flex flex-col gap-8">
                  {renderSectionPrefix ? renderSectionPrefix(section) : null}
                  {section.units.map(unit =>
                    unit.type === 'list' ? (
                      <CreateListUnit
                        key={unit.id}
                        unit={unit}
                        kit={kit}
                        newItem={optionId => newListItem(unit.id, optionId)}
                        icon={renderUnitIcon?.(unit) ?? null}
                      />
                    ) : (
                      <CreateFieldsUnit
                        key={unit.id}
                        unit={unit}
                        kit={kit}
                        icon={renderUnitIcon?.(unit) ?? null}
                      />
                    )
                  )}
                </div>
              </ScopeSectionView>
            ))}
          </div>
          <AnimatePresence>
            {form.formState.errors.root?.message && (
              <motion.div
                initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
                animate={{
                  opacity: 1,
                  height: 'auto',
                  overflow: 'visible'
                }}
                exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
                transition={{ duration: 0.2 }}
              >
                <Alert variant="destructive" data-element="server-error">
                  <AlertCircle />
                  <AlertTitle className="text-base">
                    Failed to create
                  </AlertTitle>
                  <AlertDescription>
                    {form.formState.errors.root.message}
                    <AlertCloseButton
                      onClick={() => form.clearErrors('root')}
                    />
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>
          <button type="submit" className="sr-only">
            Submit
          </button>
        </FormRootElement>
      </FieldContextProvider>
    </Form>
  );
}

function CreateFieldsUnit<M extends KindMap<M>>({
  unit,
  kit,
  icon
}: {
  unit: FieldsUnit<AnySpec<M>>;
  kit: FormKit<M>;
  icon: ReactNode;
}) {
  const base = `${unitPath(unit.id)}.values`;
  const showRequired = unit.fields.some(field => field.required);
  return (
    <div className="flex flex-col gap-2" data-testid={`schema-unit-${unit.id}`}>
      <UnitHeading title={unit.title} required={showRequired} icon={icon} />
      <div
        className={cn(
          'flex flex-col gap-2',
          // Fields start at the heading's text, as in the details pane.
          icon !== null && 'pl-[calc(var(--spacing)*7)]'
        )}
      >
        {unit.fields.map(field => (
          <RenderField
            key={field.key}
            kit={kit}
            spec={field}
            path={fieldPath(base, field.key)}
            hideLabel={unit.fields.length === 1}
          />
        ))}
      </div>
    </div>
  );
}

function CreateListUnit<M extends KindMap<M>>({
  unit,
  kit,
  newItem,
  icon
}: {
  unit: ListUnit<AnySpec<M>>;
  kit: FormKit<M>;
  newItem(optionId: string | null): UnitValues<M>;
  icon: ReactNode;
}) {
  return (
    // The rows keep room above of their own (`ListRowsField`).
    <div className="flex flex-col gap-1" data-testid={`schema-unit-${unit.id}`}>
      <UnitHeading title={unit.title} required={false} icon={icon} />
      <div className={cn(icon !== null && 'pl-[calc(var(--spacing)*7)]')}>
        <ListRowsField
          unit={unit}
          name={`${unitPath(unit.id)}.items`}
          kit={kit}
          newItem={newItem}
        />
      </div>
    </div>
  );
}

/**
 * Internal RHF shape. Unit ids are mapped to path-safe keys; field keys are
 * mapped to path-safe keys inside `values`. Row ids are carried as strings
 * so RHF can keep them in the array item.
 */
type UnitShape<M extends KindMap<M>> =
  | { type: 'fields'; values: UnitValues<M> }
  | { type: 'list'; items: ListFormRow<M>[] };
type FormShape<M extends KindMap<M>> = Record<string, UnitShape<M>>;

/** Every field of the form, where it sits, with the values of its unit or row. */
function schemaChecks<M extends KindMap<M>>(
  schema: EntitySchema<AnySpec<M>>,
  data: FormShape<M>
): FieldCheck<M>[] {
  return schema.sections.flatMap(section =>
    section.units.flatMap(unit => {
      const base = unitPath(unit.id);
      const raw = data[base];
      // A unit that appeared after mount is validated once the effect above
      // has given it its defaults; until then it has no shape.
      if (unit.type === 'list') {
        if (raw?.type !== 'list') return [];
        return raw.items.flatMap((item, index) => {
          const values = readRowValues(unit.item.fields, item.values);
          return unit.item.fields.map(field => ({
            path: `${base}.items.${index}.values.${fieldPath('', field.key)}`,
            spec: field,
            values
          }));
        });
      }
      if (raw?.type !== 'fields') return [];
      const values = readRowValues(unit.fields, raw.values);
      return unit.fields.map(field => ({
        path: `${base}.values.${fieldPath('', field.key)}`,
        spec: field,
        values
      }));
    })
  );
}

function toUnitShape<M extends KindMap<M>>(
  unit: CreateFormValues<M>[string]
): UnitShape<M> {
  if (unit.type === 'fields') {
    return { type: 'fields', values: toValuesShape(unit.values) };
  }
  return {
    type: 'list',
    items: unit.items.map(item => ({
      rowKey: rowIdUtils.key(item.rowId),
      values: toValuesShape(item.values)
    }))
  };
}

function emptyUnitShape<M extends KindMap<M>>(
  type: 'fields' | 'list'
): UnitShape<M> {
  return type === 'fields'
    ? { type: 'fields', values: {} }
    : { type: 'list', items: [] };
}

function toFormShape<M extends KindMap<M>>(
  values: CreateFormValues<M>
): FormShape<M> {
  const out: FormShape<M> = {};
  for (const [unitId, unit] of Object.entries(values)) {
    out[unitPath(unitId)] = toUnitShape(unit);
  }
  return out;
}

/** Maps RHF values back to unit ids / field keys using the schema as the source of truth. */
function fromFormShape<M extends KindMap<M>>(
  schema: EntitySchema<AnySpec<M>>,
  data: FormShape<M>
): CreateFormValues<M> {
  const out: CreateFormValues<M> = {};
  for (const section of schema.sections) {
    for (const unit of section.units) {
      const raw = data[unitPath(unit.id)];
      if (!raw) continue;
      if (unit.type === 'list') {
        out[unit.id] = {
          type: 'list',
          items: (raw.type === 'list' ? raw.items : []).map(item => ({
            rowId: rowIdUtils.local(),
            values: readRowValues(unit.item.fields, item.values)
          }))
        };
      } else {
        out[unit.id] = {
          type: 'fields',
          values: readRowValues(
            unit.fields,
            raw.type === 'fields' ? raw.values : {}
          )
        };
      }
    }
  }
  return out;
}
